import React, { useState, useEffect, useRef } from 'react';
import { Modal, List, Avatar, Spin, Empty, Input, Button, message, Dropdown } from 'antd';
import { UserOutlined, SendOutlined, MessageOutlined } from '@ant-design/icons';
import { getChatMessages, markSessionAsRead } from '../../../../api/chat';
import { getApartmentDetail } from '../../../../api/apartment';
import { getPhraseList } from '../../../../api/phrase';
import ApartmentModal from '../../../ApartmentList/components/ApartmentModal';
import './index.css';

const { TextArea } = Input;

const ChatModal = ({ visible, session, onClose, wsRef }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [pageNum, setPageNum] = useState(1);
  const [pageSize] = useState(20);
  const [hasMore, setHasMore] = useState(true);
  const messagesEndRef = useRef(null);
  const [detailVisible, setDetailVisible] = useState(false);
  const [detailRecord, setDetailRecord] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [phrases, setPhrases] = useState([]);

  // 获取历史消息
  const fetchMessages = async (page = 1) => {
    if (!session?.id) return;

    try {
      setLoading(true);
      const result = await getChatMessages({
        sessionId: session.id,
        pageNum: page,
        pageSize
      });

      if (result.code === 200 && result.data) {
        // 反转数组顺序，让老消息在上面，新消息在下面
        const reversedData = [...result.data].reverse();
        
        if (page === 1) {
          setMessages(reversedData);
        } else {
          // 加载更多时，将更老的消息添加到前面
          setMessages([...reversedData, ...messages]);
        }
        setHasMore(result.data.length === pageSize);
      } else {
        message.error(result.msg || '获取消息失败');
      }
    } catch (error) {
      console.error('获取消息失败:', error);
      message.error('获取消息失败');
    } finally {
      setLoading(false);
    }
  };

  // 获取常用语列表
  const fetchPhrases = async () => {
    try {
      const result = await getPhraseList();
      if (result.code === 200 && result.data) {
        setPhrases(result.data);
      }
    } catch (error) {
      console.error('获取常用语失败:', error);
    }
  };

  // 当modal打开或session变化时，重新加载消息并标记已读
  useEffect(() => {
    if (visible && session?.id) {
      setPageNum(1);
      fetchMessages(1);
      fetchPhrases(); // 获取常用语列表
      // 标记会话为已读
      markSessionAsRead(session.id).catch(err => {
        console.error('标记已读失败:', err);
      });
    }
  }, [visible, session?.id]);

  // 滚动到底部
  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    if (messages.length > 0) {
      scrollToBottom();
    }
  }, [messages]);

  // 监听WebSocket消息，实时刷新对话
  useEffect(() => {
    console.log('=== WebSocket监听器初始化 ===');
    console.log('wsRef存在:', !!wsRef?.current);
    console.log('visible:', visible);
    console.log('session.id:', session?.id);
    
    if (!wsRef?.current || !visible || !session?.id) {
      console.log('❌ WebSocket监听器未启动，条件不满足');
      return;
    }

    console.log('✅ WebSocket监听器已启动，等待消息...');

    const handleWebSocketMessage = (event) => {
      console.log('========================================');
      console.log('📨 ChatModal收到WebSocket消息');
      console.log('原始数据:', event.data);
      console.log('当前会话ID:', session.id);
      console.log('时间:', new Date().toLocaleTimeString());
      
      try {
        const data = JSON.parse(event.data);
        console.log('解析后的数据:', data);
        
        // 检查是否是消息类型的数据
        if (data.sessionId !== undefined) {
          console.log('消息中的sessionId:', data.sessionId);
          console.log('sessionId类型:', typeof data.sessionId, typeof session.id);
          console.log('sessionId匹配:', data.sessionId == session.id); // 使用==而不是===，防止类型不同
          
          // 如果消息属于当前会话，刷新消息列表
          if (data.sessionId == session.id) {  // 使用==宽松比较
            console.log('✅ sessionId匹配，刷新消息列表');
            fetchMessages(1);
          } else {
            console.log('⚠️ sessionId不匹配，跳过刷新');
          }
        } else {
          // 如果没有sessionId字段，也尝试刷新（可能是其他格式的消息）
          console.log('⚠️ 消息中没有sessionId字段，尝试刷新列表');
          fetchMessages(1);
        }
      } catch (error) {
        console.log('⚠️ 消息不是JSON格式:', error.message);
        console.log('使用兜底策略：直接刷新消息列表');
        fetchMessages(1);
      }
      console.log('========================================');
    };

    wsRef.current.addEventListener('message', handleWebSocketMessage);
    console.log('✅ 已添加WebSocket message事件监听器');

    return () => {
      console.log('🧹 清理WebSocket监听器');
      wsRef.current?.removeEventListener('message', handleWebSocketMessage);
    };
  }, [visible, session?.id, wsRef]);

  // 加载更多消息
  const loadMore = () => {
    if (!loading && hasMore) {
      const nextPage = pageNum + 1;
      setPageNum(nextPage);
      fetchMessages(nextPage);
    }
  };

  // 点击房源卡片打开房源详情
  const handleOpenHouseDetail = async (houseId) => {
    if (!houseId) return;
    try {
      setDetailLoading(true);
      const result = await getApartmentDetail(houseId);
      if (result.code === 200 && result.data) {
        setDetailRecord(result.data);
        setDetailVisible(true);
      } else {
        message.error(result.msg || '获取房源详情失败');
      }
    } catch (error) {
      console.error('获取房源详情失败:', error);
      message.error('获取房源详情失败');
    } finally {
      setDetailLoading(false);
    }
  };

  // 渲染消息内容
  const renderMessageContent = (msg) => {
    switch (msg.messageType) {
      case 1: // 文本消息
        return <div className="message-text">{msg.content}</div>;
      case 2: // 房源卡片
        return (
          <div className="message-house-card">
            {msg.houseCard ? (
              <div
                className="house-card house-card-clickable"
                role="button"
                tabIndex={0}
                onClick={() => {
                  const houseId = msg.houseCard?.houseId;
                  if (houseId) handleOpenHouseDetail(houseId);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const houseId = msg.houseCard?.houseId;
                    if (houseId) handleOpenHouseDetail(houseId);
                  }
                }}
              >
                <div className="house-card-image">
                  {msg.houseCard.image ? (
                    <img src={getFullUrl(msg.houseCard.image)} alt={msg.houseCard.title || '房源'} />
                  ) : (
                    <div className="house-card-image-placeholder">暂无图片</div>
                  )}
                </div>
                <div className="house-card-info">
                  <div className="house-card-title">{msg.houseCard.title || '房源信息'}</div>
                  <div className="house-card-address">{msg.houseCard.address || '—'}</div>
                  <div className="house-card-price">
                    {msg.houseCard.price ? `¥${msg.houseCard.price}/月` : '—'}
                  </div>
                </div>
              </div>
            ) : (
              <div className="message-text">{msg.content}</div>
            )}
          </div>
        );
      case 3: // 图片消息
        return (
          <div className="message-image">
            <img src={getFullUrl(msg.content)} alt="图片消息" />
          </div>
        );
      default:
        return <div className="message-text">{msg.content}</div>;
    }
  };

  // 格式化时间（显示日期+时间）
  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const date = new Date(timeStr);
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      const messageDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
      const diffDays = Math.floor((today - messageDate) / (1000 * 60 * 60 * 24));
      
      // 今天：只显示时间
      if (diffDays === 0) {
        return date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // 昨天
      else if (diffDays === 1) {
        return `昨天 ${date.toLocaleTimeString('zh-CN', {
          hour: '2-digit',
          minute: '2-digit'
        })}`;
      }
      // 今年：显示月-日 时间
      else if (date.getFullYear() === now.getFullYear()) {
        return date.toLocaleString('zh-CN', {
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
      // 往年：显示完整日期时间
      else {
        return date.toLocaleString('zh-CN', {
          year: 'numeric',
          month: '2-digit',
          day: '2-digit',
          hour: '2-digit',
          minute: '2-digit'
        });
      }
    } catch (error) {
      return timeStr;
    }
  };

  // 获取完整URL（添加host前缀）
  const getFullUrl = (url) => {
    if (!url) return '';
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url;
    }
    return `${window.location.origin}${url}`;
  };

  // 发送消息（通过WebSocket）
  const handleSendMessage = () => {
    console.log('========================================');
    console.log('📤 准备通过WebSocket发送消息');
    
    if (!inputValue.trim()) {
      console.log('❌ 消息内容为空');
      message.warning('请输入消息内容');
      return;
    }

    if (!session?.userId || !session?.id) {
      console.log('❌ 会话信息不完整');
      console.log('session.userId:', session?.userId);
      console.log('session.id:', session?.id);
      message.error('会话信息不存在');
      return;
    }

    // 检查WebSocket连接状态
    if (!wsRef?.current) {
      console.log('❌ WebSocket引用不存在');
      message.error('连接未建立');
      return;
    }

    if (wsRef.current.readyState !== WebSocket.OPEN) {
      console.log('❌ WebSocket未连接');
      console.log('当前状态:', wsRef.current.readyState);
      console.log('状态说明: 0=CONNECTING, 1=OPEN, 2=CLOSING, 3=CLOSED');
      message.error('连接已断开，请刷新页面重试');
      return;
    }

    console.log('✅ WebSocket连接正常');
    console.log('对方用户ID (receiverId):', session.userId);
    console.log('会话ID:', session.id);
    console.log('消息内容:', inputValue.trim());

    try {
      setSending(true);
      
      const messageData = {
        messageType: 1,
        content: inputValue.trim(),
        houseId: null,
        receiverId: session.userId,   // 接收者ID（对方用户的userId）
        sessionId: session.id          // 会话ID
      };

      console.log('📨 发送的消息对象:', messageData);
      const messageJson = JSON.stringify(messageData);
      console.log('📨 JSON字符串:', messageJson);
      
      // 通过WebSocket发送消息
      wsRef.current.send(messageJson);
      
      console.log('✅ 消息已通过WebSocket发送');
      message.success('发送成功');
      
      // 清空输入框
      setInputValue('');
      
      // 注意：消息会通过WebSocket回传，届时会自动刷新列表
      console.log('⏳ 等待WebSocket返回消息并自动刷新列表');
      
    } catch (error) {
      console.error('❌ 发送消息异常:', error);
      message.error('发送失败：' + error.message);
    } finally {
      setSending(false);
      console.log('========================================');
    }
  };

  // 按Enter发送消息
  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // 选择常用语并直接发送
  const handleSelectPhrase = (content) => {
    if (!content.trim()) {
      message.warning('常用语内容为空');
      return;
    }

    if (!session?.userId || !session?.id) {
      message.error('会话信息不存在');
      return;
    }

    if (!wsRef?.current || wsRef.current.readyState !== WebSocket.OPEN) {
      message.error('连接已断开，请刷新页面重试');
      return;
    }

    try {
      const messageData = {
        messageType: 1,
        content: content.trim(),
        houseId: null,
        receiverId: session.userId,
        sessionId: session.id
      };

      wsRef.current.send(JSON.stringify(messageData));
      message.success('发送成功');
      
    } catch (error) {
      console.error('❌ 发送消息异常:', error);
      message.error('发送失败：' + error.message);
    }
  };

  // 构建常用语菜单项
  const phraseMenuItems = phrases.map((phrase, index) => ({
    key: phrase.id || index,
    label: (
      <div style={{ 
        maxWidth: '300px', 
        whiteSpace: 'normal', 
        wordBreak: 'break-word',
        padding: '4px 0'
      }}>
        {phrase.content}
      </div>
    ),
    onClick: () => handleSelectPhrase(phrase.content)
  }));

  return (
    <>
    <Modal
      title={
        <div className="chat-modal-title">
          <Avatar src={session?.userAvatar} icon={<UserOutlined />} />
          <div className="user-info">
            <div className="user-name">{session?.userName}</div>
            <div className="user-id">ID: {session?.userId}</div>
          </div>
        </div>
      }
      open={visible}
      onCancel={onClose}
      width={700}
      footer={null}
      className="chat-modal"
    >
      <div className="chat-container">
        <div className="messages-wrapper">
          {hasMore && !loading && messages.length >= pageSize && (
            <div className="load-more-btn">
              <Button type="link" onClick={loadMore}>
                加载更多消息
              </Button>
            </div>
          )}
          
          <Spin spinning={loading}>
            {messages.length === 0 && !loading ? (
              <Empty description="暂无消息记录" />
            ) : (
              <List
                className="messages-list"
                dataSource={messages}
                renderItem={(msg) => (
                  <div
                    key={msg.id}
                    className={`message-item ${msg.senderType === 2 ? 'message-admin' : 'message-user'}`}
                  >
                    <div className="message-avatar">
                      <Avatar src={msg.senderAvatar} icon={<UserOutlined />} />
                    </div>
                    <div className="message-content-wrapper">
                      <div className="message-header">
                        <span className="sender-name">{msg.senderName}</span>
                        <span className="message-time">{formatTime(msg.createTime)}</span>
                      </div>
                      <div className="message-bubble">
                        {renderMessageContent(msg)}
                      </div>
                    </div>
                  </div>
                )}
              />
            )}
          </Spin>
          <div ref={messagesEndRef} />
        </div>

        {/* 输入框区域 */}
        <div className="input-area">
          <TextArea
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyPress={handleKeyPress}
            placeholder="输入消息内容..."
            autoSize={{ minRows: 2, maxRows: 4 }}
            disabled={sending}
          />
          <div className="input-actions">
            <Dropdown
              menu={{ items: phraseMenuItems }}
              placement="topRight"
              disabled={sending || phrases.length === 0}
            >
              <Button
                icon={<MessageOutlined />}
                disabled={sending || phrases.length === 0}
                title="选择常用语"
              >
                常用语
              </Button>
            </Dropdown>
            <Button
              type="primary"
              icon={<SendOutlined />}
              onClick={handleSendMessage}
              loading={sending}
              disabled={!inputValue.trim()}
            >
              发送
            </Button>
          </div>
        </div>
      </div>
    </Modal>

    <ApartmentModal
      visible={detailVisible}
      record={detailRecord}
      onCancel={() => {
        setDetailVisible(false);
        setDetailRecord(null);
      }}
      onSuccess={() => {}}
      detailOnly
    />
    </>
  );
};

export default ChatModal;
